import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useQuery, useQueryCache } from "react-query";
import { Segment, Header, Icon, Label, Popup, Button } from "semantic-ui-react";
import ConnectedCheckoutForm from "../../../components/ConnectedCheckoutForm";
import { postPermitCheckout } from "../../../queries/permits";
import { getStripeSupportStatus } from "../../../queries/payments";
import _ from "lodash";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

interface IPastCharges {
  details: boolean;
  id: string;
  balance?: number;
  activeFees?: number;
  paid?: number;
  feeList?: string[];
  paymentRequested?: boolean;
  subtotal?: number;
}

export const PermitsPayment = ({
  feeList,
  id,
  activeFees,
  paid = 0,
  details,
  balance,
  paymentRequested = true,
  subtotal,
}: IPastCharges) => {
  const [success, setSuccess] = useState<boolean>(false);
  const networkHeaders = useNetworkHeaders();
  const queryCache = useQueryCache();
  const stripeSupportQuery = useQuery(["stripe.support.status"], getStripeSupportStatus(networkHeaders));
  const isStripeConfigured = _.get(stripeSupportQuery, "data['supports-stripe']", false);
  const { site } = useParams<{ site: string }>();
  const permitsForm = useForm({
    defaultValues: {
      id,
    },
  });

  const hideBalance = !paymentRequested;
  const feesPaid = balance == 0 && paid > 0;

  return (
    <>
      <FormProvider {...permitsForm}>
        {!details && <Header>Fees</Header>}
        <Segment basic={!details} padded className='public-portal-grid-segment'>
          {details && (
            <>
              <Header as='h2' style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Fees
                <div>
                  {feesPaid && (
                    <Label circular color='green'>
                      Paid
                    </Label>
                  )}
                  {!hideBalance && (
                    <Popup
                      trigger={
                        <Button
                          style={{ margin: "0px 0px 0px 16px" }}
                          icon='print'
                          secondary
                          onClick={() => window.open(`/${site}/public/permits/details/${id}/print`)}
                        />
                      }
                      content='Print'
                      inverted
                      position='bottom right'
                      mouseEnterDelay={300}
                    />
                  )}
                </div>
              </Header>
            </>
          )}
          {/* display message if hiding balance or if balance is 0 */}
          {hideBalance && (
            <div
              style={{
                display: "flex",
                textAlign: "center",
                justifyContent: "center",
                padding: "36px",
                fontSize: "16px",
              }}
            >
              No fees are due at this time, we will notify you if anything changes.
            </div>
          )}
          {!hideBalance && isStripeConfigured && (
            <ConnectedCheckoutForm
              feeList={feeList}
              activeFees={activeFees}
              paid={paid}
              checkout={postPermitCheckout(networkHeaders, id)}
              onSuccess={async () => {
                setSuccess(true);
                await queryCache.invalidateQueries("permits");
              }}
              balance={balance}
              waitForPending={true}
              subtotal={subtotal}
              hideCheckoutForm={success}
            />
          )}

          {/* payment succeded message */}
          {success && (
            <Segment textAlign='left'>
              <Icon size='large' color='green' name='check circle' />
              <Header style={{ display: "inline" }}> Payment Successful! </Header>
            </Segment>
          )}
        </Segment>
      </FormProvider>
    </>
  );
};

export default PermitsPayment;
